import { EquipmentSlot, EquipmentItem } from '@shared/types';
import { GameItem, CurrencyItem, MaterialItem } from '@shared/items/Item';
import type { SavedItem, SavedInventoryData } from '@client/save/SaveManager';
import type { EffectStone } from '@shared/skills/SkillCreator';
import type { CraftingCurrencyType } from '@shared/crafting/CraftingSystem';
import { debugLog } from '@shared/utils/Logger';
import { getItemIconPath, SLOT_FALLBACK_ICONS } from '@client/ui/ItemIcons';
import { getAudioManager } from '@client/audio/AudioManager';
import { t } from '@locales/index';
import { getBaseById, getDefaultGridSize, getCategoryForSlot, resolveLegacyBaseId } from '@shared/items/BaseItemRegistry';

const SLOTS_PER_TAB = 60; // 6 rows x 10 columns
const MAX_TABS = 10;
const INITIAL_TABS = 1;
const TAB_PURCHASE_COST = 10000; // Gold cost per new tab

// Special currency slot types
export type CurrencySlotType =
  | 'upgrade_orb'      // Yükseltme Küresi
  | 'chaos_orb'        // Kaos Küresi
  | 'divine_orb'       // İlahi Küre
  | 'regal_orb'        // Asil Küre
  | 'exalted_orb'      // Yüce Küre
  | 'socket_orb'       // Yuva Küresi
  | 'link_orb'         // Bağ Küresi
  | 'transmute_orb'    // Dönüşüm Küresi
  | 'alteration_orb'   // Değişim Küresi
  | 'augment_orb';     // Güçlendirme Küresi

export function getCurrencySlotInfo(type: CurrencySlotType): { name: string; icon: string; color: string } {
  const info: Record<CurrencySlotType, { nameKey: string; icon: string; color: string }> = {
    upgrade_orb: { nameKey: 'currency.upgradeOrb', icon: '⬆', color: '#88ff88' },
    chaos_orb: { nameKey: 'currency.chaosOrb', icon: '☯', color: '#ff6666' },
    divine_orb: { nameKey: 'currency.divineOrb', icon: '✦', color: '#ffdd44' },
    regal_orb: { nameKey: 'currency.regalOrb', icon: '♔', color: '#4488ff' },
    exalted_orb: { nameKey: 'currency.exaltedOrb', icon: '★', color: '#ff9944' },
    socket_orb: { nameKey: 'currency.socketOrb', icon: '◉', color: '#88ddff' },
    link_orb: { nameKey: 'currency.linkOrb', icon: '∞', color: '#dd88ff' },
    transmute_orb: { nameKey: 'currency.transmuteOrb', icon: '⟳', color: '#aaaaaa' },
    alteration_orb: { nameKey: 'currency.alterationOrb', icon: '↺', color: '#99bbff' },
    augment_orb: { nameKey: 'currency.augmentOrb', icon: '+', color: '#88ff88' },
  };
  const entry = info[type];
  return { name: t(entry.nameKey), icon: entry.icon, color: entry.color };
}

// Keep backward compatibility
export const CURRENCY_SLOT_INFO: Record<CurrencySlotType, { icon: string; color: string }> = {
  upgrade_orb: { icon: '⬆', color: '#88ff88' },
  chaos_orb: { icon: '☯', color: '#ff6666' },
  divine_orb: { icon: '✦', color: '#ffdd44' },
  regal_orb: { icon: '♔', color: '#4488ff' },
  exalted_orb: { icon: '★', color: '#ff9944' },
  socket_orb: { icon: '◉', color: '#88ddff' },
  link_orb: { icon: '∞', color: '#dd88ff' },
  transmute_orb: { icon: '⟳', color: '#aaaaaa' },
  alteration_orb: { icon: '↺', color: '#99bbff' },
  augment_orb: { icon: '+', color: '#88ff88' },
};

export class Inventory {
  // Multi-tab inventory
  private tabs: (GameItem | null)[][];
  private tabCount: number = INITIAL_TABS;
  private activeTab: number = 0;

  // Equipment
  private equipment: Map<EquipmentSlot, EquipmentItem | null>;
  private gold: number = 0;

  // Special currency slots (infinite stacking, 1 slot per type)
  private currencySlots: Map<CurrencySlotType, number> = new Map();

  // Crafting currency slots (used by crafting system)
  private craftingCurrencies: Map<CraftingCurrencyType, number> = new Map();

  // Effect stones for skills
  private effectStones: EffectStone[] = [];

  constructor() {
    // Initialize first tab
    this.tabs = [new Array(SLOTS_PER_TAB).fill(null)];
    this.equipment = new Map();

    // Initialize equipment slots
    const slots: EquipmentSlot[] = [
      'head', 'chest', 'gloves', 'belt', 'boots',
      'weapon', 'offhand', 'amulet', 'ring1', 'ring2', 'cape'
    ];
    slots.forEach(slot => this.equipment.set(slot, null));

    // Initialize currency slots
    const currencyTypes: CurrencySlotType[] = [
      'upgrade_orb', 'chaos_orb', 'divine_orb', 'regal_orb', 'exalted_orb',
      'socket_orb', 'link_orb', 'transmute_orb', 'alteration_orb', 'augment_orb'
    ];
    currencyTypes.forEach(type => this.currencySlots.set(type, 0));
  }

  // Tab management
  getTabCount(): number {
    return this.tabCount;
  }

  getActiveTab(): number {
    return this.activeTab;
  }

  setActiveTab(tab: number): void {
    if (tab >= 0 && tab < this.tabCount) {
      this.activeTab = tab;
    }
  }

  canPurchaseTab(): boolean {
    return this.tabCount < MAX_TABS && this.gold >= TAB_PURCHASE_COST;
  }

  getTabPurchaseCost(): number {
    return TAB_PURCHASE_COST;
  }

  purchaseTab(): boolean {
    if (!this.canPurchaseTab()) return false;

    this.gold -= TAB_PURCHASE_COST;
    this.tabs.push(new Array(SLOTS_PER_TAB).fill(null));
    this.tabCount++;
    return true;
  }

  // Currency slot management
  getCurrencyAmount(type: CurrencySlotType): number {
    return this.currencySlots.get(type) || 0;
  }

  addCurrency(type: CurrencySlotType, amount: number = 1): void {
    const current = this.currencySlots.get(type) || 0;
    this.currencySlots.set(type, current + amount);
  }

  removeCurrency(type: CurrencySlotType, amount: number = 1): boolean {
    const current = this.currencySlots.get(type) || 0;
    if (current < amount) return false;
    this.currencySlots.set(type, current - amount);
    return true;
  }

  getAllCurrencySlots(): Map<CurrencySlotType, number> {
    return new Map(this.currencySlots);
  }

  // Crafting currency management
  getCraftingCurrencyCount(type: CraftingCurrencyType): number {
    return this.craftingCurrencies.get(type) || 0;
  }

  addCraftingCurrency(type: CraftingCurrencyType, amount: number = 1): void {
    const current = this.craftingCurrencies.get(type) || 0;
    this.craftingCurrencies.set(type, current + amount);
  }

  removeCraftingCurrency(type: CraftingCurrencyType, amount: number = 1): boolean {
    const current = this.craftingCurrencies.get(type) || 0;
    if (current < amount) return false;
    this.craftingCurrencies.set(type, current - amount);
    return true;
  }

  // Effect stone management
  getEffectStones(): EffectStone[] {
    return [...this.effectStones];
  }

  addEffectStone(stone: EffectStone): void {
    this.effectStones.push(stone);
  }

  removeEffectStone(stoneId: string): EffectStone | null {
    const index = this.effectStones.findIndex(s => s.id === stoneId);
    if (index >= 0) {
      return this.effectStones.splice(index, 1)[0];
    }
    return null;
  }

  getEffectStoneById(stoneId: string): EffectStone | null {
    return this.effectStones.find(s => s.id === stoneId) || null;
  }

  addItem(item: GameItem, autoEquip: boolean = false): boolean {
    // Handle currency separately
    if ('currencyType' in item) {
      const currency = item as CurrencyItem;
      if (currency.currencyType === 'altun' || currency.currencyType === 'gold') {
        this.gold += currency.stackSize;
        return true;
      }
    }

    // Check if it's a stackable material that goes to currency slots
    if ('materialType' in item) {
      const mat = item as MaterialItem;
      const currencyType = this.getMaterialCurrencyType(mat.materialType);
      if (currencyType) {
        this.addCurrency(currencyType, mat.stackSize);
        return true;
      }
    }

    // Auto-equip if enabled and slot is empty
    if (autoEquip && 'slot' in item) {
      const equipItem = item as EquipmentItem;
      const currentEquipped = this.equipment.get(equipItem.slot);
      if (!currentEquipped) {
        this.equipItem(equipItem);
        return true;
      }
    }

    // Find first empty slot in active tab
    const activeItems = this.tabs[this.activeTab];
    let emptyIndex = activeItems.findIndex(i => i === null);

    // If active tab is full, try other tabs
    if (emptyIndex === -1) {
      for (let t = 0; t < this.tabCount; t++) {
        if (t === this.activeTab) continue;
        emptyIndex = this.tabs[t].findIndex(i => i === null);
        if (emptyIndex !== -1) {
          this.tabs[t][emptyIndex] = item;
          return true;
        }
      }
      debugLog('Inventory', 'Full!');
      return false;
    }

    activeItems[emptyIndex] = item;
    return true;
  }

  // Map material types to currency slot types
  private getMaterialCurrencyType(materialType: string): CurrencySlotType | null {
    const mapping: Record<string, CurrencySlotType> = {
      'upgrade_orb': 'upgrade_orb',
      'chaos_orb': 'chaos_orb',
      'divine_orb': 'divine_orb',
      'regal_orb': 'regal_orb',
      'exalted_orb': 'exalted_orb',
      'socket_orb': 'socket_orb',
      'link_orb': 'link_orb',
      'transmute_orb': 'transmute_orb',
      'alteration_orb': 'alteration_orb',
      'augment_orb': 'augment_orb',
    };
    return mapping[materialType] || null;
  }

  removeItem(index: number, tab?: number): GameItem | null {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return null;

    const items = this.tabs[targetTab];
    if (index < 0 || index >= items.length) return null;

    const item = items[index];
    items[index] = null;
    return item;
  }

  /**
   * Permanently destroy an item at the given slot index.
   * Quest items cannot be destroyed.
   * Returns true if the item was successfully destroyed.
   */
  destroyItem(index: number, tab?: number): boolean {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return false;

    const items = this.tabs[targetTab];
    if (index < 0 || index >= items.length) return false;

    const item = items[index];
    if (!item) return false;

    // Quest items cannot be destroyed
    if ('itemType' in item && (item as { itemType: string }).itemType === 'quest') {
      return false;
    }

    items[index] = null;
    debugLog('Inventory', `Destroyed item: ${item.name}`);
    return true;
  }

  getItem(index: number, tab?: number): GameItem | null {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return null;
    return this.tabs[targetTab][index] || null;
  }

  getAllItems(tab?: number): (GameItem | null)[] {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return [];
    return [...this.tabs[targetTab]];
  }

  // Get all items across all tabs (for filtering/sorting)
  getAllItemsAllTabs(): { item: GameItem; tab: number; index: number }[] {
    const result: { item: GameItem; tab: number; index: number }[] = [];
    for (let t = 0; t < this.tabCount; t++) {
      for (let i = 0; i < this.tabs[t].length; i++) {
        const item = this.tabs[t][i];
        if (item) {
          result.push({ item, tab: t, index: i });
        }
      }
    }
    return result;
  }

  getGold(): number {
    return this.gold;
  }

  addGold(amount: number): void {
    this.gold += amount;
  }

  removeGold(amount: number): boolean {
    if (this.gold >= amount) {
      this.gold -= amount;
      return true;
    }
    return false;
  }

  equipItem(item: EquipmentItem, targetSlot?: EquipmentSlot): EquipmentItem | null {
    // Determine the actual slot to use
    let slot = item.slot;

    // For rings, allow equipping to either ring slot
    if (targetSlot && this.isRingSlot(item.slot) && this.isRingSlot(targetSlot)) {
      slot = targetSlot;
    } else if (targetSlot && item.slot === targetSlot) {
      slot = targetSlot;
    }

    const previousItem = this.equipment.get(slot) || null;
    this.equipment.set(slot, item);
    getAudioManager().play('item_equip');
    return previousItem;
  }

  unequipItem(slot: EquipmentSlot): EquipmentItem | null {
    const item = this.equipment.get(slot) || null;
    this.equipment.set(slot, null);
    return item;
  }

  // Helper to check if a slot is a ring slot
  isRingSlot(slot: EquipmentSlot): boolean {
    return slot === 'ring1' || slot === 'ring2';
  }

  // Check if an item can be equipped to a specific slot
  canEquipToSlot(item: EquipmentItem, targetSlot: EquipmentSlot): boolean {
    // Exact match
    if (item.slot === targetSlot) return true;
    // Rings can go to either ring slot
    if (this.isRingSlot(item.slot) && this.isRingSlot(targetSlot)) return true;
    return false;
  }

  getEquippedItem(slot: EquipmentSlot): EquipmentItem | null {
    return this.equipment.get(slot) || null;
  }

  getAllEquipped(): Map<EquipmentSlot, EquipmentItem | null> {
    return new Map(this.equipment);
  }

  getUsedSlots(tab?: number): number {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return 0;
    return this.tabs[targetTab].filter(i => i !== null).length;
  }

  getTotalUsedSlots(): number {
    let count = 0;
    for (let t = 0; t < this.tabCount; t++) {
      count += this.tabs[t].filter(i => i !== null).length;
    }
    return count;
  }

  getMaxSlots(): number {
    return SLOTS_PER_TAB;
  }

  getTotalMaxSlots(): number {
    return SLOTS_PER_TAB * this.tabCount;
  }

  isFull(): boolean {
    for (let t = 0; t < this.tabCount; t++) {
      if (this.tabs[t].some(i => i === null)) return false;
    }
    return true;
  }

  isTabFull(tab?: number): boolean {
    const targetTab = tab ?? this.activeTab;
    if (targetTab < 0 || targetTab >= this.tabCount) return true;
    return !this.tabs[targetTab].some(i => i === null);
  }

  // Calculate total stat bonuses from equipment, separated by calcType
  getEquipmentStats(): { flat: Record<string, number>; increased: Record<string, number> } {
    const flat: Record<string, number> = {};
    const increased: Record<string, number> = {};

    for (const [, item] of this.equipment) {
      if (!item) continue;

      const allMods = [...(item.implicit || []), ...(item.prefixes || []), ...(item.suffixes || [])];
      for (const mod of allMods) {
        if (mod.calcType === 'increased' || mod.calcType === 'more') {
          increased[mod.type] = (increased[mod.type] || 0) + mod.value;
        } else {
          flat[mod.type] = (flat[mod.type] || 0) + mod.value;
        }
      }
    }

    return { flat, increased };
  }

  // Serialize all tabs for saving
  serializeItems(): SavedInventoryData {
    const savedTabs: SavedItem[][] = [];

    for (let t = 0; t < this.tabCount; t++) {
      const tabItems: SavedItem[] = [];
      for (const item of this.tabs[t]) {
        if (item && 'slot' in item) {
          const eq = item as EquipmentItem;
          tabItems.push({
            baseId: eq.baseId || eq.name.toLowerCase().replace(/\s/g, '_'),
            rarity: eq.rarity,
            name: eq.name,
            level: eq.level,
            slot: eq.slot,
            implicit: (eq.implicit || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
            prefixes: (eq.prefixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
            suffixes: (eq.suffixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
          });
        }
      }
      savedTabs.push(tabItems);
    }

    // Serialize currency slots
    const currencies: Record<string, number> = {};
    for (const [type, amount] of this.currencySlots) {
      currencies[type] = amount;
    }
    // Serialize crafting currencies
    for (const [type, amount] of this.craftingCurrencies) {
      currencies[`craft_${type}`] = amount;
    }

    return {
      tabs: savedTabs,
      tabCount: this.tabCount,
      currencies,
      gold: this.gold,
    };
  }

  // Serialize equipment for saving
  serializeEquipment(): Record<string, SavedItem | null> {
    const savedEquip: Record<string, SavedItem | null> = {};
    for (const [slot, item] of this.equipment) {
      if (item) {
        savedEquip[slot] = {
          baseId: item.baseId || item.name.toLowerCase().replace(/\s/g, '_'),
          rarity: item.rarity,
          name: item.name,
          level: item.level,
          slot: item.slot,
          implicit: (item.implicit || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
          prefixes: (item.prefixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
          suffixes: (item.suffixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier, ...(m.calcType ? { calcType: m.calcType } : {}) })),
        };
      } else {
        savedEquip[slot] = null;
      }
    }
    return savedEquip;
  }

  // Deserialize items from save
  deserializeItems(savedData: SavedInventoryData | SavedItem[]): void {
    // Handle old format (array of items)
    if (Array.isArray(savedData)) {
      this.tabs = [new Array(SLOTS_PER_TAB).fill(null)];
      this.tabCount = 1;

      for (let i = 0; i < savedData.length && i < SLOTS_PER_TAB; i++) {
        const saved = savedData[i];
        if (saved) {
          this.tabs[0][i] = this.createItemFromSaved(saved, i);
        }
      }
      return;
    }

    // Handle new format
    this.tabCount = savedData.tabCount || 1;
    this.tabs = [];
    this.gold = savedData.gold || 0;

    for (let t = 0; t < this.tabCount; t++) {
      this.tabs.push(new Array(SLOTS_PER_TAB).fill(null));
      const tabItems = savedData.tabs[t] || [];
      for (let i = 0; i < tabItems.length && i < SLOTS_PER_TAB; i++) {
        const saved = tabItems[i];
        if (saved) {
          this.tabs[t][i] = this.createItemFromSaved(saved, i);
        }
      }
    }

    // Load currencies (validate types before setting)
    if (savedData.currencies && typeof savedData.currencies === 'object') {
      const validCurrencySlots = new Set<string>(Object.keys(CURRENCY_SLOT_INFO));
      const validCraftingTypes = new Set<string>([
        'demir_parcasi', 'ruh_damlasi', 'kam_boyasi', 'bereket_tozu',
        'ugur_tozu', 'temizleme_suyu', 'sabitleme_tasi', 'koruma_muhru',
        'guclu_cekic', 'kutsal_yag',
      ]);
      for (const [type, amount] of Object.entries(savedData.currencies)) {
        if (typeof amount !== 'number' || amount < 0) continue;
        if (type.startsWith('craft_')) {
          // Crafting currency (prefixed with 'craft_')
          const craftType = type.substring(6);
          if (validCraftingTypes.has(craftType)) {
            this.craftingCurrencies.set(craftType as CraftingCurrencyType, amount);
          } else {
            debugLog('Inventory', `Skipping unknown crafting currency type on load: ${craftType}`);
          }
        } else {
          if (validCurrencySlots.has(type)) {
            this.currencySlots.set(type as CurrencySlotType, amount);
          } else {
            debugLog('Inventory', `Skipping unknown currency type on load: ${type}`);
          }
        }
      }
    }
  }

  private createItemFromSaved(saved: SavedItem, index: number): EquipmentItem {
    const slot = (saved.slot || 'weapon') as EquipmentSlot;
    const rarity = saved.rarity as EquipmentItem['rarity'];

    // Resolve base for new fields (backward compat)
    const resolvedBaseId = resolveLegacyBaseId(saved.baseId);
    const baseDef = getBaseById(resolvedBaseId);

    // New format: separate implicit/prefixes/suffixes arrays
    if (saved.implicit || saved.prefixes || saved.suffixes) {
      return {
        id: `item_${Date.now()}_${index}`,
        name: saved.name,
        rarity,
        slot,
        level: saved.level,
        icon: getItemIconPath(saved.baseId, rarity) || SLOT_FALLBACK_ICONS[slot],
        implicit: (saved.implicit || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
        prefixes: (saved.prefixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
        suffixes: (saved.suffixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
        enhancement: 0,
        // New fields with backward compat defaults
        baseId: resolvedBaseId,
        category: baseDef?.category || getCategoryForSlot(slot),
        gridSize: baseDef?.gridSize || getDefaultGridSize(slot),
        defenseType: baseDef?.defenseType,
        weaponSubType: baseDef?.weaponSubType,
        ilvl: saved.level,
      };
    }

    // Legacy format: single mods array (backwards compatibility)
    const mods = saved.mods || [];
    return {
      id: `item_${Date.now()}_${index}`,
      name: saved.name,
      rarity,
      slot,
      level: saved.level,
      icon: getItemIconPath(saved.baseId, rarity) || SLOT_FALLBACK_ICONS[slot],
      implicit: mods.slice(0, 1).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
      prefixes: mods.slice(1, 3).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
      suffixes: mods.slice(3).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) })),
      enhancement: 0,
      // New fields with backward compat defaults
      baseId: resolvedBaseId,
      category: baseDef?.category || getCategoryForSlot(slot),
      gridSize: baseDef?.gridSize || getDefaultGridSize(slot),
      defenseType: baseDef?.defenseType,
      weaponSubType: baseDef?.weaponSubType,
      ilvl: saved.level,
    };
  }

  // Deserialize equipment from save
  deserializeEquipment(savedEquip: Record<string, SavedItem | null>): void {
    const validSlots: Set<string> = new Set([
      'head', 'chest', 'gloves', 'belt', 'boots',
      'weapon', 'offhand', 'amulet', 'ring1', 'ring2', 'cape'
    ]);
    for (const [slotKey, saved] of Object.entries(savedEquip)) {
      if (!validSlots.has(slotKey)) continue;
      const slot = slotKey as EquipmentSlot;
      if (saved) {
        const rarity = saved.rarity as EquipmentItem['rarity'];
        let implicit: { type: string; value: number; tier: number }[];
        let prefixes: { type: string; value: number; tier: number }[];
        let suffixes: { type: string; value: number; tier: number }[];

        // New format: separate arrays
        if (saved.implicit || saved.prefixes || saved.suffixes) {
          implicit = (saved.implicit || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
          prefixes = (saved.prefixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
          suffixes = (saved.suffixes || []).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
        } else {
          // Legacy format: single mods array
          const mods = saved.mods || [];
          implicit = mods.slice(0, 1).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
          prefixes = mods.slice(1, 3).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
          suffixes = mods.slice(3).map(m => ({ type: m.type, value: m.value, tier: m.tier || 1, ...(m.calcType ? { calcType: m.calcType as import('@shared/types').ModifierCalcType } : {}) }));
        }

        // Resolve base for new fields (backward compat)
        const resolvedBaseId = resolveLegacyBaseId(saved.baseId);
        const baseDef = getBaseById(resolvedBaseId);

        const item: EquipmentItem = {
          id: `equip_${Date.now()}_${slot}`,
          name: saved.name,
          rarity,
          slot: slot,
          level: saved.level,
          icon: getItemIconPath(saved.baseId, rarity) || SLOT_FALLBACK_ICONS[slot],
          implicit,
          prefixes,
          suffixes,
          enhancement: 0,
          // New fields with backward compat defaults
          baseId: resolvedBaseId,
          category: baseDef?.category || getCategoryForSlot(slot),
          gridSize: baseDef?.gridSize || getDefaultGridSize(slot),
          defenseType: baseDef?.defenseType,
          weaponSubType: baseDef?.weaponSubType,
          ilvl: saved.level,
        };
        this.equipment.set(slot, item);
      } else {
        this.equipment.set(slot, null);
      }
    }
  }

  // Move item between slots (with optional tab change)
  moveItem(fromIndex: number, toIndex: number, fromTab?: number, toTab?: number): boolean {
    const srcTab = fromTab ?? this.activeTab;
    const dstTab = toTab ?? this.activeTab;

    if (srcTab < 0 || srcTab >= this.tabCount) return false;
    if (dstTab < 0 || dstTab >= this.tabCount) return false;

    const srcItem = this.tabs[srcTab][fromIndex];
    const dstItem = this.tabs[dstTab][toIndex];

    // Swap items
    this.tabs[dstTab][toIndex] = srcItem;
    this.tabs[srcTab][fromIndex] = dstItem;

    return true;
  }

  // Sort items in current tab
  sortItems(sortBy: 'rarity' | 'level' | 'type' | 'name', ascending: boolean = false): void {
    if (this.activeTab < 0 || this.activeTab >= this.tabCount) return;
    const items = this.tabs[this.activeTab].filter(i => i !== null) as GameItem[];

    const rarityOrder = ['normal', 'magic', 'rare', 'set', 'legendary'];

    items.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'rarity':
          const aRarity = 'rarity' in a ? rarityOrder.indexOf((a as EquipmentItem).rarity) : 0;
          const bRarity = 'rarity' in b ? rarityOrder.indexOf((b as EquipmentItem).rarity) : 0;
          comparison = bRarity - aRarity; // Higher rarity first
          break;
        case 'level':
          const aLevel = 'level' in a ? (a as EquipmentItem).level : 0;
          const bLevel = 'level' in b ? (b as EquipmentItem).level : 0;
          comparison = bLevel - aLevel; // Higher level first
          break;
        case 'type':
          const aSlot = 'slot' in a ? (a as EquipmentItem).slot : '';
          const bSlot = 'slot' in b ? (b as EquipmentItem).slot : '';
          comparison = aSlot.localeCompare(bSlot);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
      }

      return ascending ? -comparison : comparison;
    });

    // Clear tab and re-add sorted items
    this.tabs[this.activeTab] = new Array(SLOTS_PER_TAB).fill(null);
    items.forEach((item, i) => {
      this.tabs[this.activeTab][i] = item;
    });
  }
}

// Re-export from SaveManager for convenience
export type { SavedItem, SavedInventoryData } from '@client/save/SaveManager';
